import theme from '@/theme';
import { Box, Button, FormControl, TextField } from '@mui/material';
import { useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import type { ConcessionCardFile, ConcessionCardType } from '..';
import { ConcessionCardExpirationDatePicker, ConcessionCardTypeTextField, ConcessionCardUpload } from '..';
import type { UseConcessionCardFormValues } from '../../../hooks/concession/useConcessionCardForm';
import { useConcessionCardForm } from '../../../hooks/concession/useConcessionCardForm';
import { transformLuxonToYearMonthDay } from '../utils';

type SubmitValues = {
  cardType: ConcessionCardType;
  cardNumber: string;
  expiryYear: number;
  expiryMonth: number;
  expiryDay: number;
  cardImage: ConcessionCardFile;
};

export type ConcessionCardFormProps = {
  onSubmit: (data: SubmitValues) => Promise<UseConcessionCardFormValues | void> | UseConcessionCardFormValues | void;
  onCancel: () => void;
  cardFormProps?: Parameters<typeof useConcessionCardForm>[0];
};

export const ConcessionCardForm = ({ onSubmit, onCancel, cardFormProps }: ConcessionCardFormProps) => {
  const formMethods = useConcessionCardForm(cardFormProps);
  const {
    handleSubmit,
    watch,
    defaultControllers: { controlCardNumber, controlCardType, controlCardExpiry, controlCardUpload },
    formState: { isSubmitting },
    setValue
  } = formMethods;

  const {
    field: { onChange: cardTypeChange, value: cardTypeValue },
    fieldState: { error: cardTypeError }
  } = controlCardType;

  const {
    field: { onChange: cardNumberOnChange, value: cardNumberValue },
    fieldState: { error: cardNumberError }
  } = controlCardNumber;

  const {
    field: { onChange: cardExpiryChange, value: cardExpiryValue },
    fieldState: { error: cardExpiryError }
  } = controlCardExpiry;

  const {
    field: { onChange: cardUploadChange, value: cardUploadFile },
    fieldState: { error: cardUploadError }
  } = controlCardUpload;

  const cardUploadFiles = useMemo(() => (cardUploadFile ? [cardUploadFile] : []), [cardUploadFile]);
  const handleDelete = (file: ConcessionCardFile) => {
    if (cardUploadFile && cardUploadFile.name === file.name) {
      setValue('cardImage', null);
    }
  };

  const transformSubmit: SubmitHandler<UseConcessionCardFormValues> = async ({
    cardExpiry,
    cardNumber,
    cardType,
    cardImage
  }) => {
    if (!cardImage || !cardExpiry || !cardType) {
      return;
    }
    const { year: expiryYear, month: expiryMonth, day: expiryDay } = transformLuxonToYearMonthDay(cardExpiry);
    const result = await onSubmit({
      cardNumber,
      cardType,
      expiryDay,
      expiryMonth,
      expiryYear,
      cardImage
    });
    // This submit handler may be a 2 stage process, e.g. upload image and then send card details. We want to put the
    // image into "uploaded" state if the first was successful, but the card details were not to avoid re-uploading.
    if (result && result.cardImage) {
      setValue('cardImage', cardImage);
    }
  };

  const cardType = watch('cardType');

  const handleImagesAccepted = async (files: File[]) => {
    cardUploadChange(files[0]); // Assume max drop size is 1
  };

  return (
    <Box component="form" onSubmit={handleSubmit(transformSubmit)} sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(6),
          minWidth: '100%',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <ConcessionCardTypeTextField
            onChange={cardTypeChange}
            value={cardTypeValue}
            error={Boolean(cardTypeError)}
            helperText={cardTypeError?.message}
            disabled={isSubmitting}
          />
        </Box>

        <TextField
          label="Card number"
          variant="outlined"
          InputLabelProps={{
            shrink: true
          }}
          placeholder="1234 5678 9"
          onChange={cardNumberOnChange}
          value={cardNumberValue}
          error={Boolean(cardNumberError)}
          helperText={cardNumberError?.message}
          disabled={isSubmitting}
          sx={{ minWidth: theme.spacing(105) }}
        />
        <ConcessionCardExpirationDatePicker
          cardType={cardType}
          onChange={cardExpiryChange}
          value={cardExpiryValue}
          disabled={isSubmitting}
          slotProps={{ textField: { error: Boolean(cardExpiryError), helperText: cardExpiryError?.message } }}
        />
        <FormControl error={Boolean(cardUploadError)}>
          <ConcessionCardUpload
            files={cardUploadFiles}
            onDropAccepted={handleImagesAccepted}
            loading={isSubmitting}
            onDelete={handleDelete}
            uploadError={cardUploadError}
          />
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'end', gap: theme.spacing(3), marginTop: theme.spacing(3) }}>
          <Button color="primary" size="large" variant="text" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button color="primary" size="large" variant="contained" type="submit" disabled={isSubmitting}>
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ConcessionCardForm;
